import type { FC, ReactNode } from 'react'
import type { StreamStatus } from '@/types'

import { cn } from '@/lib/utils'
import Sparkle from '@/components/landing/decor/sparkle'

interface StreamStatusBadgeProps {
    status: StreamStatus
    compact?: boolean
    className?: string
}

const LABELS: Record<StreamStatus, { full: string, short: string }> = {
    live: { full: "ON AIR", short: "ON AIR" },
    upcoming: { full: "UPCOMING", short: "NEXT" },
    archive: { full: "ARCHIVE", short: "NEW" },
    latest: { full: "LATEST", short: "NEW" }
}

// LIVE / UPCOMING / ARCHIVE marks shared by the STREAM cards and the floating card.
// A live stream gets the slowly blinking ON AIR text with a small glinting ✦.
const StreamStatusBadge: FC<StreamStatusBadgeProps> = ({ status, compact = false, className }): ReactNode => {
    const label = LABELS[status]

    if (status === 'live') {
        return (
            <span className={cn('inline-flex items-center gap-1.5 rounded-full bg-coral px-2.5 py-1 font-mono text-[10px] font-medium tracking-[0.22em] text-white shadow-[0_8px_22px_-10px_rgba(200,65,47,0.85)]', className)}>
                <Sparkle className='size-2.5 text-[#ffe9a8] motion-safe:animate-glint' />
                <span className='motion-safe:animate-onair'>{label.full}</span>
            </span>
        )
    }

    return (
        <span
            className={cn(
                'inline-flex items-center rounded-full px-2.5 py-1 font-mono text-[10px] tracking-[0.22em] ring-1 backdrop-blur-sm',
                status === 'upcoming' ? 'bg-mist/85 text-ink ring-ink/30' : 'bg-mist/75 text-ink-soft ring-ink/15',
                className
            )}
        >
            {compact ? label.short : label.full}
        </span>
    )
}

export default StreamStatusBadge
